import {
  Component,
  ElementRef,
  Renderer2,
  effect,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { GoogleMap, GoogleMapsModule } from '@angular/google-maps';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';

import { AppSettings } from '../../app-settings';
import { CountrySettings } from '../../interfaces/app-settings.interface';
import { OrderStore } from '../../order-store';
import { MapService } from '../../services/map.service';
import { MatButtonModule } from '@angular/material/button';

@Component({
  selector: 'app-address-map',
  imports: [
    FormsModule,
    GoogleMapsModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatButtonModule,
  ],
  templateUrl: './address-map.html',
  styleUrl: './address-map.scss',
})
export class AddressMap {
  private readonly mapService = inject(MapService);
  private readonly renderer = inject(Renderer2);
  private readonly store = inject(OrderStore);
  private readonly router = inject(Router);

  private readonly addresstext =
    viewChild.required<ElementRef<HTMLInputElement>>('addresstext');
  private readonly map = viewChild.required(GoogleMap);

  private readonly routeExtras =
    (this.router.getCurrentNavigation()?.extras.state as Record<
      string,
      unknown
    >) ?? {};

  readonly userAddress = signal('');
  readonly mapAddress = signal<{ latitude: number; longitude: number } | null>(
    null,
  );
  readonly city = signal<unknown>(null);
  readonly isMessage = signal(true);
  readonly zoom = 14;
  readonly center = signal<google.maps.LatLngLiteral>(
    AppSettings.settings['default'].latlng,
  );
  readonly options: google.maps.MapOptions = {
    zoomControl: true,
    disableDefaultUI: false,
    mapTypeControl: false,
    fullscreenControl: false,
    disableDoubleClickZoom: true,
    scrollwheel: false,
    streetViewControl: false,
  };

  readonly urlParams = this.store.urlParams;
  readonly order = this.store.order;

  private geoBtn?: HTMLElement;
  private snackbarTimeout?: ReturnType<typeof setTimeout>;
  private appSettings: CountrySettings = AppSettings.settings['default'];

  constructor() {
    effect(() => {
      const center = this.mapService.changeCenter();
      if (center) {
        this.setCenter(center);
      }
    });

    effect(() => {
      const status = this.mapService.geolocateUser();
      if (!this.geoBtn) {
        return;
      }
      if (status === 'loading') {
        this.geoBtn.classList.add('spin');
      }
      if (status === 'success') {
        this.geoBtn.classList.add('selected');
        this.geoBtn.classList.remove('spin');
      }
      if (status === 'error') {
        this.geoBtn.classList.remove('spin');
      }
    });

    effect(() => {
      const order = this.order();
      if (!order) {
        return;
      }
      this.city.set(order.city);
      if (AppSettings.settings[order.country]) {
        this.appSettings = AppSettings.settings[order.country];
      }
      this.onMapReady();
    });
  }

  private getPlaceAutocomplete(): void {
    const autocomplete = new google.maps.places.Autocomplete(
      this.addresstext().nativeElement,
      {
        componentRestrictions: { country: this.order()?.country ?? '' },
      },
    );
    google.maps.event.addListener(autocomplete, 'place_changed', () => {
      const place = autocomplete.getPlace();
      if (place?.geometry?.location) {
        const lat = place.geometry.location.lat();
        const lng = place.geometry.location.lng();
        this.setCenter(`${lat},${lng}`);
        this.handleGeoCode(lat, lng);
      }
    });
  }

  private handleGeoCode(lat: number, lng: number): void {
    this.mapService.geocode(lat, lng).then((result) => {
      if (result.status === 'success' && result.payload) {
        this.userAddress.set(result.payload.address);
        this.mapAddress.set(result.payload.location);
        this.city.set(result.payload.city);
        this.isMessage.set(false);
      } else {
        this.showErrorMessage(result);
      }
    });
  }

  private showErrorMessage(result: {
    type?: 'geolocation' | 'geocoding';
    error?: number;
  }): void {
    if (!result.type || result.error === undefined) {
      return;
    }
    this.snackbar(this.appSettings.errors[result.type][result.error]);
  }

  private snackbar(msg: string): void {
    clearTimeout(this.snackbarTimeout);
    const el = document.getElementById('snackbar');
    if (!el) {
      return;
    }
    el.className = 'show';
    el.innerText = msg;
    this.snackbarTimeout = setTimeout(() => {
      el.className = 'hide';
      setTimeout(() => {
        el.className = el.className.replace('hide', '');
        el.innerText = '';
      }, 500);
    }, 3000);
  }

  private setGeoIcon(): void {
    const centerControlDiv = document.createElement('div');
    centerControlDiv.id = 'geoButton';
    const controlUI = document.createElement('div');
    controlUI.className = 'controlPosition';
    controlUI.title = 'My Position';
    centerControlDiv.appendChild(controlUI);
    const controlText = document.createElement('div');
    controlText.id = 'myPosition';
    controlText.innerHTML = '<i class="material-icons">gps_fixed</i>';
    this.geoBtn = centerControlDiv;
    controlUI.appendChild(controlText);
    this.renderer.listen(this.geoBtn, 'click', () => {
      this.mapService.getCurrentPosition().then((result) => {
        if (result.status !== 'success') {
          this.showErrorMessage(result);
        }
      });
    });
  }

  private onMapReady(): void {
    this.getPlaceAutocomplete();
    if (navigator.geolocation) {
      this.setGeoIcon();
    }
    this.mapService.ready();
    if (this.geoBtn) {
      this.geoBtn.style.display = 'block';
    }
    const confirmedData = this.store.confirmedData();
    if (confirmedData) {
      this.mapAddress.set(confirmedData.location);
      this.userAddress.set(confirmedData.address);
      if (confirmedData.addressToSave) {
        this.city.set(confirmedData.addressToSave);
      }
      if (confirmedData.location) {
        setTimeout(() => {
          this.setCenter(
            `${confirmedData.location.latitude},${confirmedData.location.longitude}`,
          );
        });
      }
    }
  }

  public delete(): void {
    this.userAddress.set('');
    this.mapAddress.set(null);
    this.isMessage.set(true);
  }

  public save(): void {
    const order = this.order();
    const mapAddress = this.mapAddress();
    this.store.setConfirmedData({
      location: mapAddress ?? undefined,
      address: this.userAddress(),
    });
    this.store.setAddressToSave({
      country: order?.country,
      city: this.city() as string,
      location: mapAddress
        ? {
            latitude: mapAddress.latitude,
            longitude: mapAddress.longitude,
          }
        : undefined,
    });
    this.router.navigate(['address-details'], {
      queryParamsHandling: 'merge',
      state: this.routeExtras,
    });
  }

  public dragEnd(): void {
    const newCenter = this.map().getCenter();
    if (!newCenter) {
      return;
    }
    const center = { lat: newCenter.lat(), lng: newCenter.lng() };
    this.center.set(center);
    this.geoBtn?.classList.remove('selected');
    this.handleGeoCode(center.lat, center.lng);
  }

  private setCenter(latLng: string): void {
    const [lat, lng] = latLng.split(',').map(parseFloat);
    const center = { lat, lng };
    this.center.set(center);
    this.map().panTo(center);
  }
}
